import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { 
  MagnifyingGlass, DotsThree, Spinner, Plus, Eye, EyeSlash, Pencil, Trash, 
  UserCircleCheck, UserCircleMinus, DownloadSimple, FileXls, X
} from '@phosphor-icons/react';
import { ROLE_LABELS } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  admin: 'bg-[#8E44AD] text-white',
  tecnoservice: 'bg-[#2C3E50] text-white',
  client_admin: 'bg-[#2980B9] text-white',
  client_employee: 'bg-[#34495E] text-white',
  client_user: 'bg-[#27AE60]/10 text-[#27AE60]',
};

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkUpdates, setBulkUpdates] = useState({ role: '', client_id: '' });
  const [bulkSaving, setBulkSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', role: 'client_user', client_id: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/users`),
        axios.get(`${API_URL}/api/admin/clients`)
      ]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', role: 'client_user', client_id: '' });
    setFormError('');
    setShowPassword(false);
    setEditingUser(null);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setFormData({ name: user.name, email: user.email, password: '', role: user.role, client_id: user.client_id || '' });
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      if (editingUser) {
        const payload = { name: formData.name, email: formData.email, role: formData.role, client_id: formData.client_id };
        if (formData.password) payload.password = formData.password;
        await axios.put(`${API_URL}/api/admin/users/${editingUser.id}`, payload);
        setUsers(users.map(u => u.id === editingUser.id ? { ...u, ...payload, password: undefined } : u));
      } else {
        const res = await axios.post(`${API_URL}/api/admin/users`, formData);
        setUsers([res.data, ...users]);
      }
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      const d = err.response?.data?.detail;
      setFormError(typeof d === 'string' ? d : Array.isArray(d) ? d.map(e => e.msg || '').join('. ') : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Deletar "${user.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${user.id}`);
      setUsers(users.filter(u => u.id !== user.id));
      setSelected(prev => { const n = new Set(prev); n.delete(user.id); return n; });
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao deletar');
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/users/${userId}/status?is_active=${!currentStatus}`);
      setUsers(users.map(u => u.id === userId ? { ...u, is_active: !currentStatus } : u));
    } catch (err) { console.error(err); }
  };

  const handleBulkImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      if (res.data.created > 0) fetchData();
    } catch (err) {
      setImportResult({ created: 0, errors: 1, error_details: [{ row: 0, email: '', error: err.response?.data?.detail || 'Erro' }] });
    } finally {
      setImporting(false);
    }
  };

  // Selection helpers
  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allFilteredSelected = filtered.length > 0 && filtered.every(u => selected.has(u.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(u => u.id)));
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    if (!window.confirm(`Deletar ${selected.size} usuario(s)? Esta acao nao pode ser desfeita.`)) return;
    setBulkSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/bulk-delete`, { user_ids: [...selected] });
      setUsers(users.filter(u => !selected.has(u.id)));
      setSelected(new Set());
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao deletar');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkUpdate = async () => {
    const updates = {};
    if (bulkUpdates.role) updates.role = bulkUpdates.role;
    if (bulkUpdates.client_id === '__remove__') updates.client_id = '';
    else if (bulkUpdates.client_id) updates.client_id = bulkUpdates.client_id;

    if (Object.keys(updates).length === 0) return;
    setBulkSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/bulk-update`, { user_ids: [...selected], updates });
      // Update local state
      setUsers(users.map(u => selected.has(u.id) ? { ...u, ...updates } : u));
      setSelected(new Set());
      setBulkDialogOpen(false);
      setBulkUpdates({ role: '', client_id: '' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao atualizar');
    } finally {
      setBulkSaving(false);
    }
  };

  if (loading) {
    return <DashboardLayout><div className="flex justify-center py-20"><Spinner size={48} className="animate-spin text-[#2C3E50]" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div data-testid="admin-users">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A252F] mb-2">Gerenciamento de Usuarios</h1>
            <p className="text-[#5C6B7A]">Crie, edite ou remova usuarios do sistema</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const res = await axios.get(`${API_URL}/api/admin/users/template`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'modelo_usuarios.xlsx';
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch (err) { alert('Erro ao baixar modelo'); }
              }}
              className="inline-flex items-center gap-1.5 border-[#BDC3C7] text-[#5C6B7A] hover:bg-[#F8F9FA]"
              data-testid="download-users-template"
            >
              <DownloadSimple size={16} /> Modelo
            </Button>
            <label className={`inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium border border-[#2980B9]/30 text-[#2980B9] hover:bg-[#2980B9]/5 transition-colors cursor-pointer ${importing ? 'opacity-50 pointer-events-none' : ''}`}>
              {importing ? <Spinner size={16} className="animate-spin" /> : <FileXls size={16} />}
              {importing ? 'Importando...' : 'Importar'}
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { handleBulkImport(e.target.files[0]); e.target.value = ''; }} data-testid="import-users-input" />
            </label>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#27AE60] hover:bg-[#219653]" data-testid="new-user-btn">
                  <Plus size={18} className="mr-2" /> Novo
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="relative px-8 pt-7 pb-5 sticky top-0 bg-white z-10">
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#27AE60] to-[#2C3E50]" />
                  <DialogHeader>
                    <DialogTitle className="text-[#1A252F] text-xl font-bold">
                      {editingUser ? 'Editar Usuario' : 'Criar Novo Usuario'}
                    </DialogTitle>
                  </DialogHeader>
                </div>
                <form onSubmit={handleSave} className="px-8 py-6 space-y-5" data-testid="user-form">
                  {formError && <div className="p-3 rounded-lg bg-[#E74C3C]/10 border border-[#E74C3C]/20" data-testid="form-error"><p className="text-sm text-[#E74C3C]">{formError}</p></div>}
                  <div className="space-y-2">
                    <Label className="text-[#1A252F] font-semibold text-sm">Nome Completo <span className="text-[#E74C3C]">*</span></Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Maria Silva" required className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60]" data-testid="user-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1A252F] font-semibold text-sm">{formData.role === 'tecnoservice' ? 'Login (nome.sobrenome)' : 'E-mail'} <span className="text-[#E74C3C]">*</span></Label>
                    <Input type="text" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={formData.role === 'tecnoservice' ? 'nome.sobrenome' : 'usuario@email.com'} required className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60]" data-testid="user-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1A252F] font-semibold text-sm">Senha {editingUser ? '(vazio = manter)' : <span className="text-[#E74C3C]">*</span>}</Label>
                    <div className="relative">
                      <Input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Min. 6 caracteres" required={!editingUser} minLength={formData.password ? 6 : 0} className="h-11 pr-12 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60]" data-testid="user-password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#95A5A6] hover:text-[#5C6B7A]">
                        {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1A252F] font-semibold text-sm">Perfil</Label>
                    <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                      <SelectTrigger className="h-11 border-[#BDC3C7] bg-[#F8F9FA]" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrador</SelectItem>
                        <SelectItem value="tecnoservice">Tecnoservice</SelectItem>
                        <SelectItem value="client_admin">Cliente Admin</SelectItem>
                        <SelectItem value="client_employee">Cliente Funcionario</SelectItem>
                        <SelectItem value="client_user">Cliente Final</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#1A252F] font-semibold text-sm">Cliente</Label>
                    <Select value={formData.client_id || "__none__"} onValueChange={(v) => setFormData({ ...formData, client_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="h-11 border-[#BDC3C7] bg-[#F8F9FA]" data-testid="user-client-select"><SelectValue placeholder="Sem vinculo" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Sem vinculo</SelectItem>
                        {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-3 pt-2 pb-2">
                    <Button type="button" variant="outline" className="flex-1 h-11 border-[#BDC3C7] text-[#5C6B7A]" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button type="submit" disabled={saving} className="flex-1 h-11 bg-[#27AE60] hover:bg-[#219653] text-white font-medium" data-testid="save-user-btn">
                      {saving ? <Spinner size={18} className="animate-spin" /> : (editingUser ? 'Salvar' : 'Criar')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Import result */}
        {importResult && (
          <div className={`mb-6 p-4 rounded-xl border-2 ${importResult.created > 0 ? 'bg-[#E8F5E9] border-[#27AE60]/30' : 'bg-[#FFF3E0] border-[#E67E22]/30'}`}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-[#1A252F]">{importResult.created > 0 ? `${importResult.created} importado(s)` : 'Nenhum importado'}</p>
                {importResult.errors > 0 && <div className="mt-1 space-y-0.5">{importResult.error_details?.slice(0,5).map((e,i) => <p key={i} className="text-xs text-[#5C6B7A]">Linha {e.row}: {e.error}</p>)}</div>}
              </div>
              <button onClick={() => setImportResult(null)} className="text-[#95A5A6] hover:text-[#5C6B7A]"><X size={16} /></button>
            </div>
          </div>
        )}

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <div className="mb-4 flex items-center gap-3 p-3 bg-[#2C3E50] rounded-xl text-white animate-in slide-in-from-top-2" data-testid="bulk-bar">
            <span className="text-sm font-medium">{selected.size} selecionado(s)</span>
            <div className="flex-1" />
            <Button size="sm" variant="secondary" className="bg-white/10 hover:bg-white/20 text-white text-xs" onClick={() => { setBulkUpdates({ role: '', client_id: '' }); setBulkDialogOpen(true); }} data-testid="bulk-edit-btn">
              <Pencil size={14} className="mr-1" /> Editar em lote
            </Button>
            <Button size="sm" variant="secondary" className="bg-[#E74C3C]/80 hover:bg-[#E74C3C] text-white text-xs" onClick={handleBulkDelete} disabled={bulkSaving} data-testid="bulk-delete-btn">
              {bulkSaving ? <Spinner size={14} className="animate-spin mr-1" /> : <Trash size={14} className="mr-1" />}
              Deletar
            </Button>
            <button onClick={() => setSelected(new Set())} className="text-white/60 hover:text-white ml-1"><X size={16} /></button>
          </div>
        )}

        {/* Bulk edit dialog */}
        <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
          <DialogContent className="max-w-md bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden">
            <div className="relative px-8 pt-7 pb-5">
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#2980B9] to-[#2C3E50]" />
              <DialogHeader>
                <DialogTitle className="text-[#1A252F] text-xl font-bold">Editar {selected.size} usuario(s)</DialogTitle>
                <p className="text-[#5C6B7A] text-sm mt-1">Campos vazios nao serao alterados</p>
              </DialogHeader>
            </div>
            <div className="px-8 py-6 space-y-5">
              <div className="space-y-2">
                <Label className="text-[#1A252F] font-semibold text-sm">Alterar Perfil</Label>
                <Select value={bulkUpdates.role || "__skip__"} onValueChange={(v) => setBulkUpdates({ ...bulkUpdates, role: v === "__skip__" ? "" : v })}>
                  <SelectTrigger className="h-11 border-[#BDC3C7] bg-[#F8F9FA]" data-testid="bulk-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__skip__">Nao alterar</SelectItem>
                    <SelectItem value="tecnoservice">Tecnoservice</SelectItem>
                    <SelectItem value="client_admin">Cliente Admin</SelectItem>
                    <SelectItem value="client_employee">Cliente Funcionario</SelectItem>
                    <SelectItem value="client_user">Cliente Final</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[#1A252F] font-semibold text-sm">Alterar Cliente</Label>
                <Select value={bulkUpdates.client_id || "__skip__"} onValueChange={(v) => setBulkUpdates({ ...bulkUpdates, client_id: v === "__skip__" ? "" : v })}>
                  <SelectTrigger className="h-11 border-[#BDC3C7] bg-[#F8F9FA]" data-testid="bulk-client-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__skip__">Nao alterar</SelectItem>
                    <SelectItem value="__remove__">Remover vinculo</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1 h-11" onClick={() => setBulkDialogOpen(false)}>Cancelar</Button>
                <Button className="flex-1 h-11 bg-[#2980B9] hover:bg-[#2471A3] text-white" onClick={handleBulkUpdate} disabled={bulkSaving || (!bulkUpdates.role && !bulkUpdates.client_id)} data-testid="bulk-save-btn">
                  {bulkSaving ? <Spinner size={18} className="animate-spin" /> : 'Aplicar'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <div className="mb-6 relative max-w-md">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C6B7A]" />
          <Input placeholder="Pesquisar usuarios..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-12 bg-white border-[#BDC3C7]" data-testid="user-search" />
        </div>

        <div className="bg-white rounded-xl border border-[#2C3E50]/10 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8F9FA]">
                <TableHead className="w-10">
                  <Checkbox checked={allFilteredSelected} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                </TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Nome</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Login</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Perfil</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Cliente</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Status</TableHead>
                <TableHead className="font-semibold text-[#1A252F] text-right">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((user) => (
                <TableRow key={user.id} className={selected.has(user.id) ? 'bg-[#2980B9]/5' : ''} data-testid={`user-row-${user.id}`}>
                  <TableCell>
                    <Checkbox checked={selected.has(user.id)} onCheckedChange={() => toggleSelect(user.id)} data-testid={`select-user-${user.id}`} />
                  </TableCell>
                  <TableCell className="font-medium text-[#1A252F]">{user.name}</TableCell>
                  <TableCell className="text-[#5C6B7A]">{user.email}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[user.role] || 'bg-gray-100'}`}>
                      {ROLE_LABELS[user.role] || user.role}
                    </span>
                  </TableCell>
                  <TableCell className="text-[#5C6B7A] text-sm">
                    {user.client_id ? (clients.find(c => c.id === user.client_id)?.name || '-') : '-'}
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.is_active !== false ? 'bg-[#27AE60]/10 text-[#27AE60]' : 'bg-[#E74C3C]/10 text-[#E74C3C]'}`}>
                      {user.is_active !== false ? 'Ativo' : 'Bloqueado'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`user-actions-${user.id}`}><DotsThree size={20} weight="bold" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(user)} className="cursor-pointer">
                          <Pencil size={16} className="mr-2 text-[#2980B9]" /> Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleStatus(user.id, user.is_active !== false)} className="cursor-pointer">
                          {user.is_active !== false ? <><UserCircleMinus size={16} className="mr-2 text-[#F39C12]" /> Bloquear</> : <><UserCircleCheck size={16} className="mr-2 text-[#27AE60]" /> Ativar</>}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDelete(user)} className="cursor-pointer text-[#E74C3C] focus:text-[#E74C3C]">
                          <Trash size={16} className="mr-2" /> Deletar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filtered.length === 0 && <div className="py-12 text-center text-[#5C6B7A]">Nenhum usuario encontrado</div>}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminUsers;
